import { createContext, useContext, useState, useEffect, useMemo, useCallback, type ReactNode } from 'react';
import { dataService } from '@/services/data-service';

export interface LanguageSetting {
  id: string;
  fieldKey: string;
  pageContext: string;
  defaultLabel: string;
  customLabel: string | null;
  createdBy?: string;
  createdDate: string;
  modifiedBy?: string;
  modifiedDate?: string;
}

// Default labels matching current UI (British English)
const defaultLabels: Record<string, string> = {
  // General Labels
  subLocation: 'Sub Location',
  subCategory: 'Sub-Category',
  // Asset - User Keys
  userKey1: 'User Key 1',
  userKey2: 'User Key 2',
  userKey3: 'User Key 3',
  userKey4: 'User Key 4',
  userKey5: 'User Key 5',
  // Asset - User Values
  userValue1: 'Insurance Value (User Value 1)',
  userValue2: 'User Value 2',
  userValue3: 'User Value 3',
  userValue4: 'User Value 4',
  userValue5: 'Residual Costs (User Value 5)',
  // Asset - User Dates
  userDate1: 'User Date 1',
  userDate2: 'User Date 2',
  userDate3: 'User Date 3',
  // Equipment - User Text
  equipmentUserText1: 'User Text 1',
  equipmentUserText2: 'User Text 2',
  equipmentUserText3: 'User Text 3',
  equipmentUserText4: 'User Text 4',
  equipmentUserText5: 'User Text 5',
  equipmentUserText6: 'User Text 6',
  // Equipment - User Values
  equipmentUserValue1: 'User Value 1',
  equipmentUserValue2: 'User Value 2',
  equipmentUserValue3: 'User Value 3',
  // Equipment - User Dates
  equipmentUserDate1: 'User Date 1',
  equipmentUserDate2: 'User Date 2',
  equipmentUserDate3: 'User Date 3',
  // Equipment - User Options
  equipmentUserOption1: 'User Option 1',
  equipmentUserOption2: 'User Option 2',
  equipmentUserOption3: 'User Option 3',
};

interface LanguageSettingsContextType {
  settings: LanguageSetting[];
  loading: boolean;
  error: Error | null;
  getLabel: (fieldKey: string) => string;
  refetch: () => Promise<void>;
}

const LanguageSettingsContext = createContext<LanguageSettingsContextType | undefined>(undefined);

export function LanguageSettingsProvider({ children }: Readonly<{ children: ReactNode }>) {
  const [settings, setSettings] = useState<LanguageSetting[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await dataService.getLanguageSettings();
      if (response.settings && response.settings.length > 0) {
        setSettings(response.settings);
      }
    } catch (err) {
      setError(err as Error);
      console.error('Failed to fetch language settings:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const getLabel = useCallback((fieldKey: string): string => {
    // Find the setting using case-insensitive comparison
    const setting = settings.find(s => 
      s.fieldKey.toLowerCase() === fieldKey.toLowerCase()
    );
    
    // Return custom label if set, otherwise return default label
    if (setting?.customLabel) {
      return setting.customLabel;
    }
    
    // Fall back to default labels
    return defaultLabels[fieldKey] ?? fieldKey;
  }, [settings]);

  const contextValue = useMemo(() => ({
    settings,
    loading,
    error,
    getLabel,
    refetch: fetchSettings,
  }), [settings, loading, error, getLabel, fetchSettings]);

  return (
    <LanguageSettingsContext.Provider value={contextValue}>
      {children}
    </LanguageSettingsContext.Provider>
  );
}

export function useLanguageSettings() {
  const context = useContext(LanguageSettingsContext);
  if (context === undefined) {
    throw new Error('useLanguageSettings must be used within LanguageSettingsProvider');
  }
  return context;
}
